import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { CheckCircle, XCircle, Eye } from "@phosphor-icons/react";

const TONE = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-300",
  rejected: "bg-rose-50 text-rose-700 border-rose-300",
  pending: "bg-amber-50 text-amber-700 border-amber-300",
};

export default function DocReviewPanel({ params, onChanged }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [target, setTarget] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/employee-docs/review-queue", { params: { ...params, status: filter } }).then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, params.tenant_id]);

  const decide = async (doc, status) => {
    setBusy(true);
    try {
      await api.patch(`/employee-docs/${doc.id}/review`, { status, comment: comment || null });
      toast.success(`Document ${status}`); setTarget(null); setComment(""); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const view = async (doc) => {
    const win = window.open("", "_blank");
    try { const { data } = await api.get(`/files/${doc.storage_path}`, { responseType: "blob" }); const url = URL.createObjectURL(data); if (win) win.location = url; else window.location.assign(url); }
    catch (e) { if (win) win.close(); toast.error(formatApiError(e)); }
  };

  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-5 space-y-3" data-testid="doc-review-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Document review</div>
          <div className="font-display text-xl font-bold">Approve or reject uploaded documents</div>
        </div>
        <div className="flex gap-1">
          {["pending", "approved", "rejected", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`text-xs px-2.5 py-1 border rounded-sm capitalize ${filter === f ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200 hover:bg-zinc-50"}`} data-testid={`doc-review-filter-${f}`}>{f}</button>
          ))}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-zinc-500 py-6 text-center" data-testid="doc-review-empty">No {filter === "all" ? "" : filter} documents.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="hr-table text-xs">
            <thead><tr><th>Employee</th><th>Document</th><th>Uploaded</th><th>Status</th><th className="text-right">Actions</th></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} data-testid={`doc-review-row-${d.id}`}>
                  <td>{d.employee_name}<div className="font-mono-plex text-[10px] text-zinc-500">{d.employee_code}</div></td>
                  <td className="capitalize">{(d.label || d.doc_type).replace(/_/g, " ")}</td>
                  <td className="font-mono-plex">{new Date(d.created_at).toLocaleDateString()}</td>
                  <td>
                    <Badge variant="outline" className={`rounded-sm capitalize ${TONE[d.review_status] || TONE.pending}`}>{d.review_status}</Badge>
                    {d.review_comment && <div className="text-[10px] text-zinc-500 mt-0.5 max-w-[220px] truncate" title={d.review_comment}>{d.review_comment}</div>}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => view(d)} className="rounded-sm mr-1" data-testid={`doc-review-view-${d.id}`}><Eye size={12} /></Button>
                    {d.review_status !== "approved" && <Button size="sm" onClick={() => decide(d, "approved")} disabled={busy} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 mr-1" data-testid={`doc-review-approve-${d.id}`}><CheckCircle size={12} className="mr-1" /> Approve</Button>}
                    {d.review_status !== "rejected" && <Button size="sm" variant="outline" onClick={() => { setTarget(d); setComment(""); }} className="rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`doc-review-reject-${d.id}`}><XCircle size={12} className="mr-1" /> Reject</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={!!target} onOpenChange={(v) => !v && setTarget(null)}>
        <DialogContent data-testid="doc-reject-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Reject document</DialogTitle></DialogHeader>
          <div className="text-sm text-zinc-600">Tell <b>{target?.employee_name}</b> what to fix for <span className="capitalize">{(target?.label || target?.doc_type || "").replace(/_/g, " ")}</span>. They will see this comment and can re-upload.</div>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="e.g. Photo is blurry — please upload a clear scan of both sides" className="rounded-sm" data-testid="doc-reject-comment" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => decide(target, "rejected")} disabled={busy || !comment.trim()} className="rounded-sm bg-rose-600 hover:bg-rose-700" data-testid="doc-reject-confirm">Reject &amp; notify</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
